import type { AdInterestSuggestion } from "../../../app/hooks/useAdCreator.js";
import type { TargetingToken } from "./TargetingField.js";
import { audienceLabel } from "./targetingFormat.js";
import styles from "./InterestSuggestions.module.scss";

/**
 * The four interests we suggest from the band's own artist profile, above the interest typeahead.
 *
 * **It exists because an empty search box is a question the band cannot answer.** Meta's interest catalogue is
 * enormous and its names are its own; a band that has never run an ad does not know that the thing to type is
 * "Pop punk" rather than their drummer's favourite record. The seeds are their comparable artists and genres,
 * which they have already written down — so the strip is the profile they filled in, made targetable.
 *
 * **Every chip names the seed it came from** (`match for Relient K`). An interest that arrived from nowhere is
 * one a band has no reason to trust or to reject, and the provenance is the only thing that lets them tell a
 * good match from a coincidence of words. The legacy carried the same field for the same reason.
 *
 * The three empties are deliberately three different sentences — see `AdInterestSuggestionsView.HasSeeds`.
 */
export function InterestSuggestions({
  suggestions,
  hasSeeds,
  loading,
  apiStatus,
  tokens,
  onChange,
}: {
  suggestions: AdInterestSuggestion[];
  /** False ⇒ the band's profile named nothing to work from; the fix is theirs, and the copy says where. */
  hasSeeds: boolean;
  loading: boolean;
  /** The Meta connection verdict this read came back with — "ok" when we actually reached the vendor. */
  apiStatus?: string | null;
  tokens: TargetingToken[];
  onChange: (next: TargetingToken[]) => void;
}) {
  const selected = new Set(tokens.map((t) => t.key));

  // A refused CONNECTION is not a failed match, and saying "no interests matched your profile" when we never
  // reached Meta blames the band's profile for our own missing token — the `last_status == "error"` family,
  // a message that misattributes its cause. The surface around this already carries the connect CTA (the ads
  // status strip), so the honest thing here is to add nothing to it.
  if (!loading && apiStatus && apiStatus !== "ok") return null;

  const toggle = (s: AdInterestSuggestion) => {
    const key = s.key ?? "";
    if (!key) return;
    // Adds exactly what the typeahead adds — the opaque key plus the words beside it. A suggestion is a
    // pre-resolved search hit, not a second kind of token.
    if (selected.has(key)) onChange(tokens.filter((t) => t.key !== key));
    else onChange([...tokens, { key, name: s.name ?? key }]);
  };

  return (
    <div className={styles.strip}>
      <span className={styles.head}>Suggested Interest from your artist profile</span>

      {loading ? (
        <p className={styles.note}>Loading suggestions from your artist profile…</p>
      ) : !hasSeeds ? (
        // The band has something to do about this one, so it says what — the vanishing-card rule: a strip that
        // simply is not there is indistinguishable from a feature nobody built.
        <p className={styles.note}>
          Add comparable artists or genres in Settings → Artist Profile and we&apos;ll suggest interests from them.
        </p>
      ) : suggestions.length === 0 ? (
        // We DID ask. Nothing for the band to fix here, so it points at the control that still works.
        <p className={styles.note}>No Meta interests matched your profile — search for your own below.</p>
      ) : (
        <ul className={styles.chips}>
          {suggestions.map((s) => {
            const key = s.key ?? "";
            const on = selected.has(key);
            return (
              <li key={key}>
                <button
                  type="button"
                  className={styles.chip}
                  data-selected={on || undefined}
                  aria-pressed={on}
                  onClick={() => toggle(s)}
                >
                  <span className={styles.chipTop}>
                    <span className={styles.chipName}>{s.name}</span>
                    {s.audienceSize ? <span className={styles.chipSize}>{audienceLabel(s.audienceSize)}</span> : null}
                  </span>
                  <span className={styles.chipSeed}>match for {s.seed}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
